import Papa from "papaparse";
import { lookupIdToName, lookupNameToId } from "../../Tags/helpers/Helpers";

export const convertUDWithTagDefaultsToDTO = (universalDescriptor) => {
  const dto = {
    UniversalDescriptorId: universalDescriptor.UniversalDescriptorId,
    UD: universalDescriptor.UD,
    Description: universalDescriptor.Description,
    IsActive: universalDescriptor.IsActive,
    TagName: universalDescriptor.TagName,
    ModbusMap: universalDescriptor.ModbusMap,
    TagDefaults: {
      TagIdentifier: universalDescriptor.TagIdentifier,
      DisplayName: universalDescriptor.DisplayName,
      MaxValue: universalDescriptor.MaxValue,
      MinValue: universalDescriptor.MinValue,
      GroupName: universalDescriptor.GroupName,
      AlarmLoLo: universalDescriptor.AlarmLoLo,
      AlarmLo: universalDescriptor.AlarmLo,
      AlarmHiHi: universalDescriptor.AlarmHiHi,
      AlarmHi: universalDescriptor.AlarmHi,
      Gain: universalDescriptor.Gain,
      Offset: universalDescriptor.Offset,
      UnitOfMeasure: universalDescriptor.UnitOfMeasure,
      DisplayFormat: universalDescriptor.DisplayFormat,
      SourceTypeId: universalDescriptor.SourceTypeId,
      DynamicProperties: universalDescriptor.DynamicProperties,
      DeviceParams: universalDescriptor.DeviceParams,
    },
  };
  return dto;
};

export const exportUDDTOsToCSV = (universalDescriptorDTOs, lookupValues) => {
  if (!universalDescriptorDTOs || universalDescriptorDTOs.length === 0) {
    return "";
  }

  const data = universalDescriptorDTOs.map((dto) => {
    const tagDefaults = dto.TagDefaults || {};
    return {
      UniversalDescriptorId: dto.UniversalDescriptorId,
      UD: dto.UD,
      Description: dto.Description,
      IsActive: dto.IsActive,
      TagName: dto.TagName,
      ModbusMap: dto.ModbusMap,
      TagIdentifier: tagDefaults.TagIdentifier,
      DisplayName: tagDefaults.DisplayName,
      MaxValue: tagDefaults.MaxValue,
      MinValue: tagDefaults.MinValue,
      GroupName: tagDefaults.GroupName,
      AlarmLoLo: tagDefaults.AlarmLoLo,
      AlarmLo: tagDefaults.AlarmLo,
      AlarmHiHi: tagDefaults.AlarmHiHi,
      AlarmHi: tagDefaults.AlarmHi,
      Gain: tagDefaults.Gain,
      Offset: tagDefaults.Offset,
      UnitOfMeasure: tagDefaults.UnitOfMeasure,
      DisplayFormat: tagDefaults.DisplayFormat,
      SourceType: lookupIdToName(
        tagDefaults.SourceTypeId,
        "TagSourceType",
        lookupValues
      ),
      DynamicProperties: tagDefaults.DynamicProperties,
      DeviceParams: tagDefaults.DeviceParams,
    };
  });

  return Papa.unparse(data, { header: true });
};

export const importUDDTOsFromCSV = (csvContent, lookupValues) => {
  const validHeaders = [
    "UniversalDescriptorId",
    "UD",
    "Description",
    "IsActive",
    "TagName",
    "ModbusMap",
    "TagIdentifier",
    "DisplayName",
    "MaxValue",
    "MinValue",
    "GroupName",
    "AlarmLoLo",
    "AlarmLo",
    "AlarmHiHi",
    "AlarmHi",
    "Gain",
    "Offset",
    "UnitOfMeasure",
    "DisplayFormat",
    "SourceType",
    "DynamicProperties",
    "DeviceParams",
  ];

  if (!csvContent || typeof csvContent !== "string") {
    return [];
  }

  const result = Papa.parse(csvContent, {
    header: true,
    skipEmptyLines: "greedy",
    dynamicTyping: true,
  });

  if (
    result.meta.fields.some((field) => validHeaders.includes(field) === false)
  ) {
    throw new Error("Invalid CSV format.");
  }

  if (result.errors.length > 0) {
    throw new Error(
      `CSV parsing errors occurred. ${result.errors.map((e) => e.message).join("\n")}`
    );
  }

  return result.data.map((row, i) => {
    let sourceTypeId = null;
    if (row.SourceType && row.SourceType !== "") {
      sourceTypeId = lookupNameToId(
        row.SourceType,
        "TagSourceType",
        lookupValues
      );
      if (!sourceTypeId) {
        throw new Error(
          `Row ${i + 2}: SourceType '${row.SourceType}' is not valid.`
        );
      }
    }
    const dto = {
      UniversalDescriptorId: 0,
      UD: row.UD,
      Description: row.Description,
      IsActive: row.IsActive,
      TagName: row.TagName,
      ModbusMap: row.ModbusMap,
      TagDefaults: {
        TagIdentifier: row.TagIdentifier,
        DisplayName: row.DisplayName,
        MaxValue: row.MaxValue,
        MinValue: row.MinValue,
        GroupName: row.GroupName,
        AlarmLoLo: row.AlarmLoLo,
        AlarmLo: row.AlarmLo,
        AlarmHiHi: row.AlarmHiHi,
        AlarmHi: row.AlarmHi,
        Gain: row.Gain,
        Offset: row.Offset,
        UnitOfMeasure: row.UnitOfMeasure,
        DisplayFormat: row.DisplayFormat,
        SourceTypeId: sourceTypeId,
        DynamicProperties: row.DynamicProperties,
        DeviceParams: row.DeviceParams,
      },
    };

    return dto;
  });
};

export const validateImportedUDDTOs = (existingUDs, importedUDs) => {
  const errors = [];

  const existingUDSet = new Set(existingUDs.map((ud) => ud.UD.toUpperCase()));
  const importedUDSet = new Set();

  importedUDs.forEach((dto, index) => {
    const i = index + 2; // Adjust for header row in CSV

    if (!dto.UD || dto.UD.trim() === "") {
      errors.push(`Row ${i}: UD is required.`);
    } else {
      const upperUD = dto.UD.toUpperCase();
      if (existingUDSet.has(upperUD)) {
        errors.push(`Row ${i}: UD '${dto.UD}' already exists.`);
      }
      if (importedUDSet.has(upperUD)) {
        errors.push(`Row ${i}: UD '${dto.UD}' is duplicated in the import.`);
      } else {
        importedUDSet.add(upperUD);
      }
    }
    if (!dto.Description || dto.Description.trim() === "") {
      errors.push(`Row ${i}: Description is required.`);
    }
    if (!dto.TagName || dto.TagName.trim() === "") {
      errors.push(`Row ${i}: TagName is required.`);
    }
    if (dto.IsActive === undefined || dto.IsActive === null) {
      errors.push(`Row ${i}: IsActive is required.`);
    }

    // Type validation

    if (typeof dto.IsActive !== "boolean") {
      errors.push(`Row ${i}: IsActive must be a boolean.`);
    }
    if (dto.ModbusMap && typeof dto.ModbusMap !== "number") {
      errors.push(`Row ${i}: ModbusMap must be a number.`);
    }

    const tagDefaults = dto.TagDefaults || {};

    if (tagDefaults.MaxValue && typeof tagDefaults.MaxValue !== "number") {
      errors.push(`Row ${i}: MaxValue must be a number.`);
    }
    if (tagDefaults.MinValue && typeof tagDefaults.MinValue !== "number") {
      errors.push(`Row ${i}: MinValue must be a number.`);
    }
    if (tagDefaults.Gain && typeof tagDefaults.Gain !== "number") {
      errors.push(`Row ${i}: Gain must be a number.`);
    }
    if (tagDefaults.Offset && typeof tagDefaults.Offset !== "number") {
      errors.push(`Row ${i}: Offset must be a number.`);
    }
    if (
      tagDefaults.TagIdentifier &&
      typeof tagDefaults.TagIdentifier !== "number"
    ) {
      errors.push(`Row ${i}: TagIdentifier must be a number.`);
    }
    if (
      tagDefaults.SourceTypeId &&
      typeof tagDefaults.SourceTypeId !== "number"
    ) {
      errors.push(`Row ${i}: SourceType is not valid.`);
    }
    if (tagDefaults.AlarmLoLo && typeof tagDefaults.AlarmLoLo !== "number") {
      errors.push(`Row ${i}: AlarmLoLo must be a number.`);
    }
    if (tagDefaults.AlarmLo && typeof tagDefaults.AlarmLo !== "number") {
      errors.push(`Row ${i}: AlarmLo must be a number.`);
    }
    if (tagDefaults.AlarmHiHi && typeof tagDefaults.AlarmHiHi !== "number") {
      errors.push(`Row ${i}: AlarmHiHi must be a number.`);
    }
    if (tagDefaults.AlarmHi && typeof tagDefaults.AlarmHi !== "number") {
      errors.push(`Row ${i}: AlarmHi must be a number.`);
    }
    // UnitOfMeasure must be a string
    if (
      tagDefaults.UnitOfMeasure &&
      typeof tagDefaults.UnitOfMeasure !== "string"
    ) {
      errors.push(`Row ${i}: UnitOfMeasure must be a string.`);
    }
  });

  return errors;
};
